/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useDispatch } from "react-redux";
// Base
import BASELoading from "../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../@BASE/core/BASESvgIcon";
import useThemeMediaQuery from "../../../../@BASE/hooks/useThemeMediaQuery";
import _ from "../../../../@lodash";
// MUI components
import { styled } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from "@mui/material/Typography";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& td, & th': {
    border: 'solid 1px #000',
    backgroundColor: 'transparent'
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const TemplateView = () => {
  // get the instance from the instance wrapper
  const { instance, loading } = useOutletContext();
  const routeParams = useParams();
  const dispatch = useDispatch();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));

  // if template is null return loading
  if (!instance || loading) {
    return <BASELoading />;
  }

  return (
    <div className="flex flex-col flex-auto w-full mt-32 mb-64 px-24 sm:px-24">
      <div className="max-w-4xl">
        {instance.image && (
          <div className="flex flex-col mb-32">
            <Grid container spacing={0}>
              <Grid item xs={12}>                
                <img
                  className="max-w-[100%] my-0 shadow-md"
                  src={`https://base-media-repo.web.app/templates/${instance.label}/mockups/Desktop.jpg`}
                  alt="Assessment Introduction Mockup"
                />
              </Grid>
            </Grid>
          </div>
        )}

        <Typography className="mt-12 text-2xl font-bold truncate">
          1. General Information
        </Typography>

        <Divider className="mt-16 mb-24" />

        <div className="flex flex-col space-y-32">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {instance.description && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    1. Activity Rationale:
                  </Typography>
                  <div className="flex items-center">
                    <div className="leading-6">{instance.description}</div>
                  </div>
                </div>
              )}
            </Grid>
            {instance.instructions && (
              <Grid item xs={12}>
                <Typography className="text-14 font-bold truncate">
                  2. Instruction Set:
                </Typography>
                <ul className="list-disc ml-16 mt-16">
                  {instance.instructions.map((item, index) => (
                    <li key={index}>
                      <Typography className="text-14 font-semibold truncate">
                        {item.title}:
                      </Typography>
                      <Typography 
                        className={`leading-6 html-reset ${item.rtl ? 'text-right' : 'text-left'}`}
                        variant="body2"
                        dangerouslySetInnerHTML={{ __html: item.content }}
                        dir={item.rtl ? 'rtl' : 'ltr'}
                      />
                    </li>
                  ))}
                </ul>
              </Grid>
            )}
          </Grid>
        </div>



        <Typography className="mt-32 text-2xl font-bold truncate">
          Development Information
        </Typography>

        <Divider className="mt-16 mb-24" />

        <div className="flex flex-col space-y-32">
          <Grid container spacing={4}>
            
            <Grid item xs={12}>
              {instance.branching && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    Branching:
                  </Typography>
                  <div className="flex items-center">
                      <div 
                        className="leading-6 html-reset"
                        dangerouslySetInnerHTML={{__html: instance.branching}}
                      />
                  </div>
                </div>
              )}
            </Grid>

            <Grid item xs={12}>
              {instance.dev_notes && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    Programming Notes:
                  </Typography>
                  <div className="flex items-center">
                    <div className="leading-6 html-reset" dangerouslySetInnerHTML={{__html: instance.dev_notes}} />
                  </div>
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              {instance.ga_notes && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    Graphic Artist Notes:
                  </Typography>
                  <div className="flex items-center">
                    <div className="leading-6 html-reset" dangerouslySetInnerHTML={{__html: instance.ga_notes}} />
                  </div>
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              {instance.accessibility && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    508 Compliance:
                  </Typography>
                  <div className="flex items-center">
                    <div className="leading-6 html-reset" dangerouslySetInnerHTML={{__html: instance.accessibility}} />
                  </div>
                </div>
              )}
              {instance.access_table && (
                <TableContainer component={Paper} className="rounded-none mt-20 bg-transparent shadow-none">
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Element</StyledTableCell>
                        <StyledTableCell>Form of Compliance</StyledTableCell>
                        <StyledTableCell>Notes</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {instance.access_table.map((row, rowIndex) => (
                        row.items.map((item, itemIndex) => (
                          <StyledTableRow key={`${rowIndex}-${itemIndex}`}>
                            {itemIndex === 0 && (
                              <StyledTableCell rowSpan={row.items.length} component="th" scope="row">{row.element}</StyledTableCell>
                            )}
                            <StyledTableCell>{item.compliance}</StyledTableCell>
                            <StyledTableCell dangerouslySetInnerHTML={{__html: item.notes}} />
                          </StyledTableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
            <Grid item xs={12}>
              {instance.info && (
                <div>
                  <Typography className="text-14 font-bold truncate">
                    Additional Information:
                  </Typography>
                  <div className="flex items-center">
                    <div className="leading-6 html-reset" dangerouslySetInnerHTML={{__html: instance.info}} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>

        <Typography className="mt-32 text-2xl font-bold truncate">
          Component Breakdown
        </Typography>

        <Divider className="mt-16 mb-24" />

        <div className="flex flex-col space-y-32">
          <Grid container spacing={4}>
            {instance.components && (
              instance.components.map((component, compIndex) => {
                const totalRows = component.elements.reduce((acc, element) => acc + element.attrs.length, 0);
                return (
                  <Grid item xs={12} key={component.image}>
                    {compIndex > 0 && (
                      <Divider className="mt-16 mb-24" />
                    )}
                    <Typography className="text-14 font-bold truncate mb-20">
                      {component.component}:
                    </Typography>
                    <img
                      className="max-w-[100%] my-0"
                      src={`https://base-media-repo.web.app/System/Templates/${component.image}.png`}
                      alt={component.image}
                    />
                    <TableContainer component={Paper} className="rounded-none mt-20 bg-transparent shadow-none">
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Parent</StyledTableCell>
                            <StyledTableCell>Element</StyledTableCell>
                            <StyledTableCell>Design Attribute</StyledTableCell>
                            <StyledTableCell>Value</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {component.elements.map((element, elementIndex) => (
                            element.attrs.map((attr, attrIndex) => (
                              <StyledTableRow key={`${elementIndex}-${attrIndex}`}>
                                {elementIndex === 0 && attrIndex === 0 && (
                                  <StyledTableCell rowSpan={totalRows} component="th" scope="row">
                                    {component.component}
                                  </StyledTableCell>
                                )}
                                {attrIndex === 0 && (
                                  <StyledTableCell rowSpan={element.attrs.length}>
                                    {element.title}
                                  </StyledTableCell>
                                )}
                                <StyledTableCell>{attr.property}</StyledTableCell>
                                <StyledTableCell>{attr.property}</StyledTableCell>
                              </StyledTableRow>
                            ))
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )
              })
            )}
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default TemplateView;
